import type { DrawingDocument } from '../model';
import type {
  MarkushDrawingDocument,
  MarkushFragment,
  MarkushScaffold,
  RGroupDefinition,
  VariablePosition
} from './markushDocument.ts';
import { connectedWithin, isPlaceholderPosition, isSingleRing, placeholderPositionAt } from './markushDocument.ts';
import {
  drawingDocumentToFragment,
  drawingDocumentToMarkushStructure,
  fragmentToDrawingDocument,
  scaffoldToDrawingDocument
} from './markushProjections.ts';
import { nextSequentialId, roundCoordinate, withoutKey } from './drawingDocumentCore.ts';
import { fragmentKey } from './markushSummary.ts';

// #587 M2: pure Markush document mutations. Like atomBondMutations/graphTopologyMutations, an
// input that cannot apply (unknown atom, a position that would break a ring attachment or a
// repeat unit, an invalid count range) returns the document unchanged rather than throwing.
// Cross-reference problems that are a normal step of authoring -- e.g. assigning R2 before R2 is
// defined -- are allowed here and reported by markushValidationIssues instead.

function withScaffold(document: MarkushDrawingDocument, scaffold: MarkushScaffold): MarkushDrawingDocument {
  return { ...document, scaffold };
}

function withPositions(
  document: MarkushDrawingDocument,
  variablePositions: readonly VariablePosition[]
): MarkushDrawingDocument {
  return { ...document, variablePositions };
}

/** Removes nodes (and their incident edges/bonds/coordinates) from a scaffold. */
function withoutNodes(scaffold: MarkushScaffold, nodeIds: ReadonlySet<string>): MarkushScaffold {
  if (nodeIds.size === 0) return scaffold;
  const edges = scaffold.graph.edges.filter((edge) => !nodeIds.has(edge.source) && !nodeIds.has(edge.target));
  const keptEdgeIds = new Set(edges.map((edge) => edge.id));
  const keepNodes = <T>(values: Readonly<Record<string, T>>) =>
    Object.fromEntries(Object.entries(values).filter(([id]) => !nodeIds.has(id)));
  return {
    graph: { nodes: scaffold.graph.nodes.filter((node) => !nodeIds.has(node.id)), edges },
    atoms: keepNodes(scaffold.atoms),
    bonds: Object.fromEntries(Object.entries(scaffold.bonds).filter(([id]) => keptEdgeIds.has(id))),
    formalCharges: keepNodes(scaffold.formalCharges),
    coordinateSet: { ...scaffold.coordinateSet, coordinates: keepNodes(scaffold.coordinateSet.coordinates) }
  };
}

/** Atoms a placeholder position may not take over: every ring atom behind an attachment point
 *  and every atom inside a repeat unit (both must stay concrete). */
function lockedAtomIds(document: MarkushDrawingDocument): ReadonlySet<string> {
  return new Set(
    document.variablePositions.flatMap((position) => {
      if (position.kind === 'attachment-point') return position.ringAtomIds;
      if (position.kind === 'repeat-unit') return position.fragmentAtomIds;
      return [];
    })
  );
}

/**
 * Applies an existing v10 DrawingDocument mutation (setAtomAtomicNumber, appendLineToDocument,
 * deleteAtom, ...) to the scaffold, then drops variable positions the edit invalidated: a
 * placeholder position whose node is gone or became a concrete atom, an attachment point that
 * lost a ring atom (its placeholder node goes with it), and repeat-unit atoms/bonds that no
 * longer exist (the unit is dropped once it spans nothing).
 */
export function updateScaffold(
  document: MarkushDrawingDocument,
  edit: (scaffold: DrawingDocument) => DrawingDocument
): MarkushDrawingDocument {
  const scaffold = drawingDocumentToMarkushStructure(edit(scaffoldToDrawingDocument(document)));
  const nodeIds = new Set(scaffold.graph.nodes.map((node) => node.id));
  const edgeIds = new Set(scaffold.graph.edges.map((edge) => edge.id));
  const isPlaceholder = (id: string) => nodeIds.has(id) && !scaffold.atoms[id];
  const brokenAttachments = new Set<string>();
  const variablePositions = document.variablePositions.flatMap((position): VariablePosition[] => {
    if (position.kind === 'repeat-unit') {
      const fragmentAtomIds = position.fragmentAtomIds.filter((id) => scaffold.atoms[id]);
      const fragmentBondIds = position.fragmentBondIds.filter((id) => edgeIds.has(id));
      return fragmentAtomIds.length > 0 ? [{ ...position, fragmentAtomIds, fragmentBondIds }] : [];
    }
    if (!isPlaceholder(position.atomId)) return [];
    if (position.kind === 'attachment-point' && position.ringAtomIds.some((id) => !scaffold.atoms[id])) {
      brokenAttachments.add(position.atomId);
      return [];
    }
    return [position];
  });
  return { ...document, scaffold: withoutNodes(scaffold, brokenAttachments), variablePositions };
}

function replacePlaceholderPosition(
  document: MarkushDrawingDocument,
  atomId: string,
  position: VariablePosition
): MarkushDrawingDocument {
  const others = document.variablePositions.filter(
    (candidate) => !(isPlaceholderPosition(candidate) && candidate.atomId === atomId)
  );
  const scaffold = document.scaffold;
  const concreteRemoved: MarkushScaffold = scaffold.atoms[atomId]
    ? {
        ...scaffold,
        atoms: withoutKey(scaffold.atoms, atomId),
        formalCharges: withoutKey(scaffold.formalCharges, atomId)
      }
    : scaffold;
  return { ...withScaffold(document, concreteRemoved), variablePositions: [...others, position] };
}

function canHoldPlaceholder(document: MarkushDrawingDocument, atomId: string): boolean {
  return document.scaffold.graph.nodes.some((node) => node.id === atomId) && !lockedAtomIds(document).has(atomId);
}

/** Turns a scaffold atom (or another placeholder) into an R-group position labelled `label`. An
 *  attachment point keeps its kind and ring and only changes label. */
export function assignRGroup(document: MarkushDrawingDocument, atomId: string, label: string): MarkushDrawingDocument {
  const rGroupLabel = label.trim();
  if (!rGroupLabel || !canHoldPlaceholder(document, atomId)) return document;
  const existing = placeholderPositionAt(document, atomId);
  if (existing?.kind === 'attachment-point') {
    return replacePlaceholderPosition(document, atomId, { ...existing, rGroupLabel });
  }
  return replacePlaceholderPosition(document, atomId, { kind: 'r-group', atomId, rGroupLabel });
}

/** Turns a scaffold atom into a variable-heteroatom position. Duplicates are removed and the list
 *  sorted; an empty or out-of-range list is rejected. */
export function assignAtomList(
  document: MarkushDrawingDocument,
  atomId: string,
  allowedAtomicNumbers: readonly number[]
): MarkushDrawingDocument {
  const allowed = [...new Set(allowedAtomicNumbers)].sort((a, b) => a - b);
  if (allowed.length === 0 || allowed.some((z) => !Number.isInteger(z) || z < 1 || z > 118)) return document;
  if (!canHoldPlaceholder(document, atomId)) return document;
  if (placeholderPositionAt(document, atomId)?.kind === 'attachment-point') return document;
  return replacePlaceholderPosition(document, atomId, { kind: 'atom-list', atomId, allowedAtomicNumbers: allowed });
}

/** Undoes a placeholder position. An R-group or atom-list node becomes a concrete neutral atom
 *  (`atomicNumber`, carbon by default); an attachment point's placeholder node is removed. */
export function clearVariablePosition(
  document: MarkushDrawingDocument,
  atomId: string,
  atomicNumber = 6
): MarkushDrawingDocument {
  const existing = placeholderPositionAt(document, atomId);
  if (!existing) return document;
  const variablePositions = document.variablePositions.filter((position) => position !== existing);
  if (existing.kind === 'attachment-point') {
    return { ...document, scaffold: withoutNodes(document.scaffold, new Set([atomId])), variablePositions };
  }
  const { scaffold } = document;
  return {
    ...document,
    scaffold: {
      ...scaffold,
      atoms: { ...scaffold.atoms, [atomId]: { atomicNumber } },
      formalCharges: { ...scaffold.formalCharges, [atomId]: 0 }
    },
    variablePositions
  };
}

/** Adds a ring-interior attachment point for `label` over one ring of concrete atoms, with its
 *  placeholder node at the ring's centroid. Rejects anything but a single simple ring. */
export function addAttachmentPoint(
  document: MarkushDrawingDocument,
  ringAtomIds: readonly string[],
  label: string
): MarkushDrawingDocument {
  const rGroupLabel = label.trim();
  const { scaffold } = document;
  if (!rGroupLabel || ringAtomIds.some((id) => !scaffold.atoms[id])) return document;
  if (!isSingleRing(scaffold.graph, ringAtomIds)) return document;
  const ringCoordinates = ringAtomIds.map((id) => scaffold.coordinateSet.coordinates[id]);
  if (ringCoordinates.some((point) => !point)) return document;
  const atomId = nextSequentialId(
    scaffold.graph.nodes.map((node) => node.id),
    'n'
  );
  const centroid = {
    x: roundCoordinate(ringCoordinates.reduce((sum, point) => sum + point!.x, 0) / ringCoordinates.length),
    y: roundCoordinate(ringCoordinates.reduce((sum, point) => sum + point!.y, 0) / ringCoordinates.length)
  };
  return {
    ...document,
    scaffold: {
      ...scaffold,
      graph: { ...scaffold.graph, nodes: [...scaffold.graph.nodes, { id: atomId }] },
      coordinateSet: {
        ...scaffold.coordinateSet,
        coordinates: { ...scaffold.coordinateSet.coordinates, [atomId]: centroid }
      }
    },
    variablePositions: [
      ...document.variablePositions,
      { kind: 'attachment-point', atomId, ringAtomIds: [...ringAtomIds], rGroupLabel }
    ]
  };
}

/**
 * Brackets `atomIds` as a repeat unit (`-(CH2)n-`), spanning every scaffold bond between two of
 * them. Replaces any repeat unit it overlaps (re-bracketing; nesting is deferred). Rejects
 * placeholders, a disconnected selection, and an invalid count range: `countMin` ≥ 0, and a
 * `countMax` (omit for open-ended) ≥ `countMin` and ≥ 1.
 */
export function setRepeatUnit(
  document: MarkushDrawingDocument,
  atomIds: readonly string[],
  countMin: number,
  countMax?: number
): MarkushDrawingDocument {
  const { scaffold } = document;
  const atoms = [...new Set(atomIds)];
  if (atoms.length === 0 || atoms.some((id) => !scaffold.atoms[id])) return document;
  if (!Number.isInteger(countMin) || countMin < 0) return document;
  if (countMax !== undefined && (!Number.isInteger(countMax) || countMax < Math.max(1, countMin))) return document;
  const inUnit = new Set(atoms);
  const edges = scaffold.graph.edges.filter((edge) => inUnit.has(edge.source) && inUnit.has(edge.target));
  const adjacency = new Map<string, string[]>(atoms.map((id) => [id, []]));
  for (const edge of edges) {
    adjacency.get(edge.source)!.push(edge.target);
    adjacency.get(edge.target)!.push(edge.source);
  }
  if (!connectedWithin(atoms, adjacency)) return document;
  const others = document.variablePositions.filter(
    (position) => position.kind !== 'repeat-unit' || !position.fragmentAtomIds.some((id) => inUnit.has(id))
  );
  return withPositions(document, [
    ...others,
    {
      kind: 'repeat-unit',
      fragmentAtomIds: atoms,
      fragmentBondIds: edges.map((edge) => edge.id),
      countMin,
      ...(countMax !== undefined ? { countMax } : {})
    }
  ]);
}

export function removeRepeatUnitContaining(document: MarkushDrawingDocument, atomId: string): MarkushDrawingDocument {
  const variablePositions = document.variablePositions.filter(
    (position) => position.kind !== 'repeat-unit' || !position.fragmentAtomIds.includes(atomId)
  );
  return variablePositions.length === document.variablePositions.length
    ? document
    : withPositions(document, variablePositions);
}

export function setRGroupDefinition(
  document: MarkushDrawingDocument,
  label: string,
  definition: RGroupDefinition
): MarkushDrawingDocument {
  const rGroupLabel = label.trim();
  if (!rGroupLabel || (definition.kind === 'enumerated' && definition.members.length === 0)) return document;
  return { ...document, rGroups: { ...document.rGroups, [rGroupLabel]: definition } };
}

/** Removes a definition even while positions still reference it -- those positions then show up
 *  as `undefined-r-group` issues, the same state as a label assigned before it is defined. */
export function removeRGroupDefinition(document: MarkushDrawingDocument, label: string): MarkushDrawingDocument {
  if (!document.rGroups[label]) return document;
  return { ...document, rGroups: withoutKey(document.rGroups, label) };
}

/** Appends a member to `label`'s enumerated list, creating the definition if there is none yet.
 *  A homology definition is left unchanged -- switching kind is setRGroupDefinition's job. */
export function addRGroupMember(
  document: MarkushDrawingDocument,
  label: string,
  member: MarkushFragment
): MarkushDrawingDocument {
  const existing = document.rGroups[label];
  if (existing?.kind === 'homology') return document;
  // #839 -- each substituent once per R-group: adding one already listed changes nothing.
  if (existing?.members.some((present) => fragmentKey(present) === fragmentKey(member))) return document;
  const members = [...(existing?.members ?? []), member];
  return setRGroupDefinition(document, label, { kind: 'enumerated', members });
}

/** Removes one member; removing the last one removes the definition (an enumerated list cannot
 *  be empty). */
export function removeRGroupMember(
  document: MarkushDrawingDocument,
  label: string,
  index: number
): MarkushDrawingDocument {
  const existing = document.rGroups[label];
  if (existing?.kind !== 'enumerated' || !existing.members[index]) return document;
  const members = existing.members.filter((_, candidate) => candidate !== index);
  return members.length === 0
    ? removeRGroupDefinition(document, label)
    : setRGroupDefinition(document, label, { kind: 'enumerated', members });
}

/** Applies an existing v10 DrawingDocument mutation to one R-group member (the member-editing
 *  counterpart of updateScaffold). `openValenceAtomId` is kept as-is; if the edit deletes that
 *  atom, markushValidationIssues reports it. */
export function updateRGroupMember(
  document: MarkushDrawingDocument,
  label: string,
  index: number,
  edit: (fragment: DrawingDocument) => DrawingDocument
): MarkushDrawingDocument {
  const existing = document.rGroups[label];
  const member = existing?.kind === 'enumerated' ? existing.members[index] : undefined;
  if (!existing || existing.kind !== 'enumerated' || !member) return document;
  const edited = drawingDocumentToFragment(edit(fragmentToDrawingDocument(member)), member.openValenceAtomId);
  const members = existing.members.map((candidate, candidateIndex) => (candidateIndex === index ? edited : candidate));
  return setRGroupDefinition(document, label, { kind: 'enumerated', members });
}
